import {useCallback} from 'react';
import {useAtomValue} from 'jotai';
import {userAtom} from '@/store/user';
import type {User} from '@/types/user';

// Helper to check if the user has a permission
// For subusers: checks role.permissions array
// For main users: allowed by default
function checkPermission(
    user: User | null,
    permission: string,
    options?: {requireFeatureNotDisabled?: string}
): boolean {
    const requireFeatureNotDisabled =
        options?.requireFeatureNotDisabled &&
        user?.features?.includes(options.requireFeatureNotDisabled);

    if (!user || requireFeatureNotDisabled) return false;

    const isSubUser = Number.isInteger(user.parent_id);
    return !(permission.endsWith('_credits') && isSubUser);
}

const USER_PERMISSIONS: Record<
    string,
    (user: User | null, ...args: unknown[]) => boolean
> = {
    // Admin access - only for Floorplanner admins
    'admin-only': (user) => user?.membership_name === 'admin',
    'view-credits': (user) =>
        checkPermission(user, 'view_credits', {
            requireFeatureNotDisabled: 'dont_show_credits',
        }),
    'buy-credits': (user) =>
        checkPermission(user, 'buy_credits', {
            requireFeatureNotDisabled: 'dont_show_credits',
        }),
    // AI Permissions - all blocked by disable_ai feature
    'ai-lifestyle-images': (user) =>
        checkPermission(user, 'ai_lifestyle_images', {
            requireFeatureNotDisabled: 'disable_ai',
        }),
    'ai-show-micro-tokens': (user) =>
        checkPermission(user, 'ai_show_micro_tokens', {
            requireFeatureNotDisabled: 'disable_ai',
        }),
    'ai-manage-micro-tokens': (user) =>
        checkPermission(user, 'ai_manage_micro_tokens', {
            requireFeatureNotDisabled: 'disable_ai',
        }),
    'ai-enhance-exports': (user) =>
        checkPermission(user, 'ai_enhance_exports', {
            requireFeatureNotDisabled: 'disable_ai',
        }),
    'ai-enhance-product-search': (user) =>
        checkPermission(user, 'ai_enhance_product_search', {
            requireFeatureNotDisabled: 'disable_ai',
        }),
    // Styles access - blocked by disable_ai feature
    'view-styles': (user) =>
        checkPermission(user, 'view_styles', {
            requireFeatureNotDisabled: 'disable_ai',
        }),
    // Linda access - available to all users except those with disable_ai
    'view-linda': (user) =>
        checkPermission(user, 'view_linda', {
            requireFeatureNotDisabled: 'disable_ai',
        }),
    // Linda chatbots management - admins and resellers only
    'linda-chatbots': (user) => {
        if (!user) return false;
        // Block if the disable_ai feature is present
        if (user.features?.includes('disable_ai')) return false;
        // Allow admins and resellers
        const isAdmin = user.membership_name === 'admin';
        const isReseller = user.membership_name === 'reseller';
        return isAdmin || isReseller;
    },
};

export function hasAccess(
    permission: string,
    user: User | null,
    ...args: unknown[]
): boolean {
    return USER_PERMISSIONS[permission]?.(user, ...args) ?? false;
}

export function usePermissions() {
    const user = useAtomValue(userAtom);

    return {
        hasAccess: useCallback(
            (permission: string, ...args: unknown[]) =>
                hasAccess(permission, user, ...args),
            [user]
        ),
        user,
    };
}
